import React, { useEffect, useRef, useState } from "react";

import { NavigationSplitViewOnTileClickedProps, NavigationSplitViewProps } from "models";

import { Flex, NavigationSplitViewGroup } from "components";

export const NavigationSplitView = ({
  title,
  groups,
  activeTileIndex,
  setActiveTileIndex,
  onTileClick,
  onTileChanged,
  className,
  dataTestId,
  height,
}: NavigationSplitViewProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const sideBodyRef = useRef<HTMLDivElement>(null);

  const handleTileClicked = ({ groupIndex, subGroupIndex, tileIndex }: NavigationSplitViewOnTileClickedProps) => {
    onTileClick?.();
    setActiveTileIndex({ groupIndex, subGroupIndex, tileIndex });
  };

  useEffect(() => {
    onTileChanged?.(activeTileIndex);
  }, [activeTileIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Event listener to detect scroll position
  useEffect(() => {
    const handleScroll = () => {
      const bodyElement = sideBodyRef.current;
      if (bodyElement) {
        // Check if the top of the scrollable body is out of view
        if (bodyElement.scrollTop > 0) {
          setIsScrolled(true);
        } else {
          setIsScrolled(false);
        }
      }
    };

    const bodyElement = sideBodyRef.current;
    if (bodyElement) {
      bodyElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (bodyElement) {
        bodyElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const internalDataTestId = dataTestId ? `${dataTestId}-navigation-split-view` : "navigation-split-view";
  const isOnboardingView = !title?.includes("Edit") ? "onboarding-view" : "";

  return (
    <Flex className={`navigation-split-view container ${className}`} style={{ height: height }} data-testid={internalDataTestId}>
      <div className={`navigation-split-view-side ${isOnboardingView}`} data-testid={`${internalDataTestId}-side`}>
        {title ? (
          <Flex className={`navigation-split-view-side-header ${isScrolled ? "scrolled" : ""}`} align="center" justify="start">
            <p data-testid={`${internalDataTestId}-side-title`}>{title}</p>
          </Flex>
        ) : null}
        <div ref={sideBodyRef} className={`navigation-split-view-side-body ${title && "with-header"}`} data-testid={`${internalDataTestId}-group-container`}>
          {groups.map((item, index) => (
            <NavigationSplitViewGroup
              key={index}
              selectedGroupIndex={activeTileIndex.groupIndex}
              selectedSubGroupIndex={activeTileIndex.subGroupIndex}
              selectedTileIndex={activeTileIndex.tileIndex}
              groupIndex={index}
              group={item}
              handleTileClicked={handleTileClicked}
              dataTestId={`${internalDataTestId}-side-group-${index}`}
            />
          ))}
        </div>
      </div>
      <Flex className="navigation-split-view main" align="center" justify="center" vertical={true}>
        {groups?.[activeTileIndex.groupIndex]?.subGroups?.[activeTileIndex.subGroupIndex]?.tiles?.[activeTileIndex.tileIndex]?.main ?? null}
      </Flex>
    </Flex>
  );
};
